/**
 * Full-page "New workflow definition" authoring route, on the Lara Canvas
 * kit shell (BRIEF Phase 2, D1).
 *
 * Replaces the old modal on the definitions list, then the old
 * name/key/description-fields-above-the-canvas layout: those fields now
 * live in the Inspector's Overview (D2), and the page fills the viewport
 * under the kit shell (D18) instead of sitting in the portal's padded
 * column. The create logic is the same as the former dialog's
 * `handleCreate` — moved, not rewritten.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { WorkflowAuthor } from "@/components/portal/workflow-flow-v2/WorkflowAuthor";
import { WorkflowEditorHeader } from "@/components/portal/workflow-flow-v2/WorkflowEditorHeader";
import { workflowJsonEquivalent } from "@/components/portal/workflow-flow-v2/workflowJsonEquivalent";
import { parseWorkflowDsl } from "@/components/portal/workflow-flow-v2/parseWorkflowDsl";
import { publishBlockingIssues, type ValidationIssue } from "@/lib/workflow-validation";
import {
  workflowsApi,
  type ReconcileResponse,
  type CreateDefinitionRequest,
  type DefinitionResponse,
} from "@/services/api/workflows";
import {
  draftPrefill,
  markDraftPublished,
  useWorkflowDraft,
} from "@/components/portal/workflows/useWorkflowDraft";
import { DEFAULT_WORKFLOW, slugify } from "./-workflowDefinition";

export const Route = createFileRoute("/portal/workflows/definitions_/new")({
  component: NewDefinitionPage,
  // `?draft=<id>` pre-fills from a Claude draft (mcp-artefact-creation-pilot D15).
  validateSearch: (search: Record<string, unknown>): { draft?: string } =>
    typeof search.draft === "string" && search.draft ? { draft: search.draft } : {},
});

const DEFINITIONS_PATH = "/portal/workflows/definitions";

/**
 * A publish whose reconciler refused a trigger, described for the author.
 *
 * The version IS saved — the DSL is valid — but a refused trigger means the
 * workflow is published with something that will never fire. That used to be
 * visible only in service logs, so the author saw a clean success. We stay on
 * the page rather than navigating away on a toast, because a toast is exactly
 * what someone misses when they are already looking at the next thing.
 */
function reconcileWarning(
  reconcile: ReconcileResponse | null | undefined,
): string | null {
  if (!reconcile) return null;
  // Keyed off the failed list, not `ok`: the server derives `ok` as "no
  // trigger failed", so checking both is a branch no test can tell apart.
  const failed = reconcile.triggers.filter((t) => t.action === "failed");
  if (failed.length === 0) return null;
  return (
    `Saved as v${reconcile.version}, but ${failed.length} trigger` +
    `${failed.length === 1 ? "" : "s"} could not be wired: ` +
    failed.map((t) => `${t.trigger_id} — ${t.error}`).join("; ") +
    ". The workflow is published, but that trigger will not fire until it is fixed."
  );
}

function NewDefinitionPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  // Authoring is owner+admin (+ platform staff) — gated the SAME way as the
  // definitions list (isAdmin || isPlatformAdmin), NOT can("*","automation").
  const { isAdmin, isPlatformAdmin } = usePermissions();
  const canManage = isAdmin || isPlatformAdmin;
  const { draft: draftId } = Route.useSearch();
  const draftLoad = useWorkflowDraft(draftId, token, canManage);

  const [name, setName] = useState("");
  const [workflowKey, setWorkflowKey] = useState("");
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);
  const [json, setJson] = useState(DEFAULT_WORKFLOW);
  // Author intent, stored in the DSL as `description`. Held as its own field
  // rather than stamped invisibly into the JSON because the Describe flow feeds
  // it: people paste identifiers and occasionally secrets into prompts, so the
  // author must see exactly what is about to be stored, and be able to edit it,
  // before it lands anywhere.
  const [description, setDescription] = useState("");
  // The last description adopted FROM the DSL. Distinguishes "the JSON carries a
  // new description" from "the JSON still carries the one we already took".
  const [lastAdopted, setLastAdopted] = useState<string | null>(null);

  /** Adopt a `description` that arrives inside the DSL — e.g. when Generate's
   * Apply drops a workflow on the canvas carrying the prompt that produced it.
   *
   * Guarded by `lastAdopted`, and that guard is load-bearing: WorkflowAuthor
   * emits the whole document on every JSON-tab keystroke, so an unguarded
   * version re-adopted the same description continuously and silently reverted
   * whatever the author had typed into the field — including a redaction. Only
   * a description that DIFFERS from the one last adopted is a new arrival. */
  const handleJsonChange = (next: string) => {
    setJson(next);
    try {
      const parsed = JSON.parse(next) as { description?: unknown };
      const incoming =
        typeof parsed.description === "string" ? parsed.description.trim() : "";
      if (incoming && incoming !== lastAdopted) {
        setDescription(incoming);
        setLastAdopted(incoming);
      }
    } catch {
      // Mid-edit JSON is routinely unparseable; nothing to adopt yet.
    }
  };
  // Pre-fill once from a Claude draft. The base stays the blank page, so the
  // draft reads as "Unpublished changes" until the person publishes it.
  const [prefilledFrom, setPrefilledFrom] = useState<string | null>(null);
  useEffect(() => {
    const draft = draftLoad.draft;
    if (!draft || prefilledFrom === draft.id) return;
    const fill = draftPrefill(draft);
    setName(fill.name);
    setWorkflowKey(fill.workflowKey);
    setKeyManuallyEdited(true);
    handleJsonChange(fill.json);
    setPrefilledFrom(draft.id);
    // handleJsonChange is re-created each render; the guard above is what
    // keeps this to one fill per draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftLoad.draft, prefilledFrom]);

  // Bumped when Publish fails a Name/Key check, so WorkflowAuthor clears the
  // selection and the Overview — where those fields live (D2) — is visible.
  const [revealOverview, setRevealOverview] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Distinct from `error`: the publish SUCCEEDED, so this is not a failure to
  // retry — it is a warning about what was left unwired.
  const [reconcileIssue, setReconcileIssue] = useState<string | null>(null);
  // Issues the ENGINE would 422 on (missing required failure exit, duplicate
  // import version key). WorkflowAuthor's Overview "Checks" lists these;
  // publishing stays disabled until they are fixed.
  // Plus the blockers only the canvas can see (duplicate exits — gate
  // R2-M1), which WorkflowAuthor reports up: the JSON has already lost them.
  const [canvasBlockers, setCanvasBlockers] = useState<ValidationIssue[]>([]);
  const publishBlockers = useMemo(
    () => [...publishBlockingIssues(json), ...canvasBlockers],
    [json, canvasBlockers],
  );
  // A half-typed JSON tab is not a workflow — the canvas shows an alert in
  // its place, and Publish waits until the document parses again.
  const jsonUnparseable = useMemo(() => parseWorkflowDsl(json) === null, [json]);

  // D3 — "No changes" vs "Unpublished changes": true once ANY field the
  // publish body is built from has moved away from a fresh page's state.
  // There is no draft endpoint, so this never claims "saved".
  // The base moves once, when a create succeeds but the page stays (a
  // reconcile warning): what was just published is then "No changes", not
  // "Unpublished changes" beside "the workflow is published" (gate S4).
  const [base, setBase] = useState({ json: DEFAULT_WORKFLOW, name: "", key: "", description: "" });
  // Set when that happens. The key now exists, so this page can never publish
  // it again (a second create is a duplicate) — new versions go through the
  // definitions list's New version.
  const [created, setCreated] = useState<DefinitionResponse | null>(null);
  const hasUnpublishedChanges =
    !workflowJsonEquivalent(json, base.json) ||
    name.trim() !== base.name ||
    workflowKey.trim() !== base.key ||
    description.trim() !== base.description;

  const backToList = () => navigate({ to: DEFINITIONS_PATH });

  if (!canManage) {
    return (
      <>
        <Helmet>
          <title>New workflow definition | Portal</title>
        </Helmet>
        <PermissionDenied
          message="Workflows are managed by organisation owners and admins. You don't have access to this area."
          backTo={DEFINITIONS_PATH}
          backLabel="Back to definitions"
        />
      </>
    );
  }

  // Hold the canvas until a requested draft has filled the page, so the
  // builder mounts on the draft's JSON rather than on the blank template.
  if (draftId && (draftLoad.loading || (draftLoad.draft && prefilledFrom !== draftLoad.draft.id))) {
    return (
      <>
        <Helmet>
          <title>New workflow definition | Portal</title>
        </Helmet>
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading draft…
        </div>
      </>
    );
  }

  const handleCreate = async () => {
    if (publishBlockers.length > 0) {
      setError(
        `Fix ${publishBlockers.length} issue${publishBlockers.length === 1 ? "" : "s"} listed in the Overview's Checks before publishing.`,
      );
      return;
    }
    if (!workflowKey.trim()) {
      setError("Workflow key is required");
      setRevealOverview((n) => n + 1);
      return;
    }
    if (!name.trim()) {
      setError("Name is required");
      setRevealOverview((n) => n + 1);
      return;
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(json) as Record<string, unknown>;
    } catch {
      setError("Workflow JSON failed to parse");
      return;
    }

    // Intent lives in the DSL so it versions with the graph — a v6 definition
    // carries v6's description, not v1's. Blank means the key is absent, not
    // present-and-empty: an empty string would render as a blank summary line.
    if (description.trim()) {
      parsed.description = description.trim();
    } else {
      delete parsed.description;
    }

    setIsSaving(true);
    setError(null);
    setReconcileIssue(null);
    try {
      const body: CreateDefinitionRequest = {
        workflow_key: workflowKey.trim(),
        name: name.trim(),
        workflow: parsed,
      };
      const result = await workflowsApi.createDefinition(
        body,
        token ?? undefined,
      );
      if (prefilledFrom) await markDraftPublished(token, prefilledFrom, result.id);
      const warning = reconcileWarning(result.reconcile);
      if (warning) {
        setBase({
          json,
          name: name.trim(),
          key: workflowKey.trim(),
          description: description.trim(),
        });
        setCreated(result);
        setReconcileIssue(warning);
        return;
      }
      toast.success(`Created ${workflowKey.trim()}`);
      void backToList();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create definition");
    } finally {
      setIsSaving(false);
    }
  };

  const header = (
    <WorkflowEditorHeader
      name={name || "New workflow"}
      workflowKey={workflowKey || undefined}
      status={created ? (created.is_enabled ? "Active" : "Paused") : "Not published"}
      isNew
    />
  );

  const publishAction = (
    <Button
      size="sm"
      aria-label="Publish v1"
      onClick={() => void handleCreate()}
      disabled={isSaving || publishBlockers.length > 0 || jsonUnparseable || created !== null}
      title={
        created
          ? `${created.workflow_key} is published — publish further versions from Definitions → New version`
          : jsonUnparseable
            ? "Fix the workflow JSON before publishing"
            : undefined
      }
    >
      {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
      Publish
      {publishBlockers.length > 0 ? (
        <span
          data-testid="publish-blocked"
          className="ml-1.5 rounded-full bg-canvas-error-tint px-1.5 text-[10px] text-canvas-error"
        >
          {publishBlockers.length}
        </span>
      ) : null}
    </Button>
  );

  // Ranked by what the person most needs to see: a failed publish, then a
  // publish that saved with an unwired trigger, then the draft-link notice.
  // The draft notice never clears, so ranking it above either outcome hid
  // that outcome entirely (gate F1, and the re-check's reconcile case).
  const canvasAlert = error ? (
    <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : reconcileIssue ? (
    <Alert data-testid="reconcile-warning" className="rounded-none border-x-0 border-t-0">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        {reconcileIssue}{" "}
        <button type="button" className="underline underline-offset-2" onClick={backToList}>
          Back to definitions
        </button>
      </AlertDescription>
    </Alert>
  ) : draftLoad.error ? (
    <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
      <AlertDescription>{draftLoad.error}</AlertDescription>
    </Alert>
  ) : prefilledFrom && !created ? (
    <Alert data-testid="draft-prefill-notice" className="rounded-none border-x-0 border-t-0">
      <Sparkles className="h-4 w-4" />
      <AlertDescription>
        Pre-filled from a draft Claude wrote. Nothing runs until you publish it —
        check every node first.
      </AlertDescription>
    </Alert>
  ) : null;

  return (
    <>
      <Helmet>
        <title>New workflow definition | Portal</title>
      </Helmet>
      {/* D18: cancel the portal layout's padding and fill it exactly — same
          technique as RelationshipModelPage.tsx, minus the TOP margin: this
          route sits under the workflows sub-nav (workflows.tsx), so above it
          is the sub-nav and its gap, not `<main>`'s padding — a negative top
          margin overlapped the sub-nav by 8px (proof P1, 2026-10-11). */}
      <div className="-mx-3 -mb-3 h-[calc(100%+0.75rem)] min-h-[560px] md:-mx-6 md:-mb-6 md:h-[calc(100%+1.5rem)]">
        <WorkflowAuthor
          onCanvasPublishBlockersChange={setCanvasBlockers}
          workflowJson={json}
          onWorkflowJsonChange={handleJsonChange}
          // Once a reconcile-warned create has published the key, the page
          // stays open on it — Executions/Config (and its runs) are live now
          // (gate R2 NIT). Before that there is no key: both tabs read
          // "Publish first".
          workflowKey={created?.workflow_key}
          header={header}
          publishAction={publishAction}
          saveStateLabel={hasUnpublishedChanges ? "Unpublished changes" : "No changes"}
          canvasAlert={canvasAlert}
          revealOverview={revealOverview}
          overviewEditable={{
            name,
            // Auto-derives the key from the name until the author edits the
            // key by hand — same behaviour as before the fields moved into
            // the Overview (D2).
            onNameChange: (next) => {
              setName(next);
              if (!keyManuallyEdited) setWorkflowKey(slugify(next));
            },
            description,
            onDescriptionChange: setDescription,
            workflowKey: {
              value: workflowKey,
              onChange: (v) => {
                setWorkflowKey(v);
                setKeyManuallyEdited(true);
              },
            },
          }}
        />
      </div>
    </>
  );
}
